import React, { useState } from 'react';
import { services } from '../data/mock';
import ServiceCarousel from './ServiceCarousel';

const ServicesSection = () => {
  const [activeService, setActiveService] = useState(null);

  const open = (service) => {
    if (service.gallery && service.gallery.length > 0) {
      setActiveService(service);
    }
  };

  return (
    <section id="services" className="bg-white py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Title */}
        <div className="mb-16">
          <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-4">
            Nos domaines d'expertise
          </h2>
          <div className="w-12 h-0.5 bg-gray-300"></div>
        </div>

        {/* Services Grid - 2x2 on desktop, 1 column on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-14">
          {services.map((service) => {
            const hasGallery = service.gallery && service.gallery.length > 0;
            const thumbs = hasGallery ? service.gallery.slice(1, 5) : [];

            return (
              <div
                key={service.id}
                data-testid={`service-card-${service.id}`}
                className={`group ${hasGallery ? 'cursor-pointer' : ''}`}
                onClick={() => open(service)}
                onKeyDown={(e) => {
                  if (hasGallery && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    open(service);
                  }
                }}
                role={hasGallery ? 'button' : undefined}
                tabIndex={hasGallery ? 0 : undefined}
                aria-label={hasGallery ? `Voir la galerie : ${service.title}` : undefined}
              >
                {/* Image */}
                <div className="relative aspect-[16/10] bg-gray-100 mb-5 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className={`w-full h-full object-cover transition-transform duration-700 ease-out ${
                      hasGallery ? 'group-hover:scale-[1.03]' : ''
                    }`}
                  />

                  {hasGallery && (
                    <>
                      {/* Subtle dark gradient on hover for legibility */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                      {/* "X photos" badge (top-right) */}
                      <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-gray-900 text-[11px] tracking-wide px-2.5 py-1 opacity-0 group-hover:opacity-100 translate-y-[-4px] group-hover:translate-y-0 transition-all duration-300">
                        {service.gallery.length} photos
                      </div>

                      {/* Staggered thumbnails fade-in at bottom */}
                      <div className="absolute bottom-3 left-3 right-3 flex gap-2 pointer-events-none">
                        {thumbs.map((src, i) => (
                          <div
                            key={i}
                            className="w-14 h-10 md:w-16 md:h-11 overflow-hidden ring-1 ring-white/40 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 ease-out"
                            style={{ transitionDelay: `${120 + i * 110}ms` }}
                          >
                            <img src={src} alt="" className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>

                      {/* "Voir la galerie" discrete hint (bottom-right over image) */}
                      <div className="absolute bottom-3 right-3 text-white text-[11px] tracking-[0.15em] uppercase opacity-0 group-hover:opacity-0 md:group-hover:opacity-0 transition-opacity duration-300 pointer-events-none" />
                    </>
                  )}
                </div>

                {/* Title */}
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className={hasGallery ? 'group-hover:text-gray-700 transition-colors' : ''}>
                    {service.title}
                  </span>
                  {hasGallery && (
                    <svg
                      className="w-4 h-4 text-gray-400 group-hover:text-gray-900 group-hover:translate-x-1 transition-all duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </h3>

                {/* Description */}
                <p className="text-gray-600 text-sm leading-relaxed">
                  {service.description}
                </p>

                {/* Subtle line */}
                <div className="mt-5 w-8 h-px bg-gray-200"></div>
              </div>
            );
          })}
        </div>
      </div>

      {activeService && (
        <ServiceCarousel
          service={activeService}
          onClose={() => setActiveService(null)}
        />
      )}
    </section>
  );
};

export default ServicesSection;
